import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, Dropdown, Grid, InputText, Picture, Text } from "~/components-shared";
import styled, { useTheme } from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import insightReportsActionCreators from "~/store/insightReports/actions";
import insightReportSelectors from "~/store/insightReports/selectors";
import ai from "~/assets/i/logos/ai.png";
import Markdown from "~/components/Markdown";
import { StatusStateEnum } from "~/types/enums";
import Icon from "../../components-shared/Icon";
const Field = styled.div`
  width: 100%;
  border: none;
  border-radius: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.shape.borderRadius.primary;
}};
  font-weight: ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.typography.fontWeight.regular;
}};
  display: flex;
  align-items: center;
  transition: box-shadow 200ms ease-in;
  box-shadow: rgb(255 255 255) 0 0 0 0, transparent 0 0 0 1px;
  ${_ref3 => {
  let {
    $outline,
    theme
  } = _ref3;
  return theme.shadows.outline(theme, $outline, theme.colors.primary.main);
}}
`;
const Response = styled.div`
  margin-bottom: ${_ref4 => {
  let {
    theme
  } = _ref4;
  return theme.spacing.step * 6;
}}px;
  position: relative;
  padding-left: 1em;
  display: block;

  &:before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    border-radius: 3px;
    background-color: ${_ref5 => {
  let {
    theme
  } = _ref5;
  return theme.colors.primary.main;
}};
    width: 3px;
    height: 100%;
  }
`;
const CloseButton = styled(Button)`
  position: absolute;
  top: 0;
  right: 0;
`;
const InsightsAssistantQuery = _ref6 => {
  let {
    reportId
  } = _ref6;
  const {
    colors
  } = useTheme();
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const [query, setQuery] = useState("");
  const response = useSelector(insightReportSelectors.getAssistantResponse(reportId));
  const status = useSelector(insightReportSelectors.getAssistantQueryStatus(reportId));
  const error = useSelector(insightReportSelectors.getAssistantQueryError(reportId));
  const suggestions = {
    0: {
      title: "All blockers and bottlenecks"
    },
    1: {
      title: "What were the most active topics?"
    },
    2: {
      title: "What topics are most urgent or need my attention right now?"
    },
    3: {
      title: "What has [member name] been up to this week"
    },
    4: {
      title: "Who’s blocked right now?"
    },
    5: {
      title: "Can you give me a full list of my action items?"
    }
  };
  const handleRequest = () => {
    if (query.length > 3) {
      dispatch(insightReportsActionCreators.assistantQueryStarted(reportId, query));
    }
  };
  const handleResponseClose = () => {
    dispatch(insightReportsActionCreators.assistantQuerySucceeded(reportId, ""));
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Dropdown, {
    direction: "bottomRight",
    customHandler: (toggle, isOpen) => /*#__PURE__*/React.createElement("form", {
      onSubmit: e => {
        e.preventDefault();
        if (isOpen) {
          toggle();
        }
        handleRequest();
      }
    }, /*#__PURE__*/React.createElement(Field, {
      onClick: toggle,
      $outline: isOpen
    }, /*#__PURE__*/React.createElement(InputText, {
      value: query,
      onChange: value => setQuery(value),
      autoComplete: "off",
      color: "translucent",
      outline: false,
      elemBefore: /*#__PURE__*/React.createElement(Picture, {
        src: ai,
        width: "1.5rem",
        height: "1.5rem",
        s: {
          mr: 2
        }
      }),
      elemAfter: /*#__PURE__*/React.createElement(Button, {
        "data-tooltip-id": "general-tooltip",
        "data-tooltip-content": t("common.actions.send"),
        onClick: e => {
          e.stopPropagation();
          handleRequest();
        },
        color: "transparent",
        rounded: true,
        disabled: query.length < 3,
        outline: false,
        loading: status === StatusStateEnum.LOADING,
        s: {
          p: 1.5,
          mr: -2,
          ml: 1
        }
      }, /*#__PURE__*/React.createElement(Icon, {
        scale: 5,
        src: "send",
        color: colors.primary.main,
        display: "block"
      })),
      placeholder: t("InsightsAssistantQuery.askJoyn"),
      clearButton: true,
      fullWidth: true
    }))),
    s: {
      mb: 5
    }
  }, _ref7 => {
    let {
      isOpen,
      onItemClick
    } = _ref7;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, /*#__PURE__*/React.createElement(Grid, {
      cols: 2,
      s: {
        p: 5
      },
      template: "1fr auto"
    }, /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
      element: "h4",
      s: {
        mb: 2
      }
    }, t("InsightsAssistantQuery.suggestions")), /*#__PURE__*/React.createElement(Box, {
      gap: 3,
      direction: "column"
    }, Object.values(suggestions).map(suggestion => /*#__PURE__*/React.createElement(Button, {
      key: suggestion.title,
      onClick: () => {
        onItemClick();
        setQuery(suggestion.title);
      },
      s: {
        px: 3,
        py: 1
      },
      color: "grey",
      fontSize: 3.5,
      outline: false,
      rounded: true
    }, suggestion.title))))));
  }), error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error), !!response && /*#__PURE__*/React.createElement(Response, null, /*#__PURE__*/React.createElement(Text, {
    display: "block",
    color: "textSecondary",
    weight: "semiBold",
    fontSize: 3
  }, t("InsightsAssistantQuery.response")), /*#__PURE__*/React.createElement(Markdown, {
    fontSize: 4,
    lineHeight: 6,
    text: response
  }), /*#__PURE__*/React.createElement(CloseButton, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("common.actions.close"),
    onClick: handleResponseClose,
    color: "transparent",
    rounded: true,
    s: {
      p: 2,
      mx: 0.5
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    scale: 5,
    src: "cross",
    color: colors.text.primary,
    display: "block"
  }))));
};
export default InsightsAssistantQuery;